import { useState, useEffect } from 'react';
import { Table, Card, Tag, Space, Input, DatePicker, Button, message, Segmented, Tabs } from 'antd';
import {
  SearchOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';
import { ClickList } from './ClickList';
import { PaynetList } from './PaynetList';

const { RangePicker } = DatePicker;

interface AtmosPayment {
  id: number;
  transactionType: 'WALLET_TOPUP' | 'WALLET_WITHDRAWAL';
  amount: number;
  description: string | null;
  atmosId: string | null;
  source: 'atmos' | 'admin' | 'other';
  timestamp: string;
  driver: {
    id: number;
    fullName: string;
    phone: string;
  } | null;
}

type AtmosView = 'all' | 'topup' | 'withdrawal';
type AtmosSource = 'all' | 'atmos' | 'admin';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('uz-UZ').format(value) + ' ' + t('common.sum');
};

const WalletLedgerTab = () => {
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [view, setView] = useState<AtmosView>('all');
  const [source, setSource] = useState<AtmosSource>('all');

  const [rows, setRows] = useState<AtmosPayment[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPayments = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/admin/payments', {
        params: {
          search: search || undefined,
          startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
          endDate: dateRange?.[1]?.format('YYYY-MM-DD'),
          page,
          limit: pageSize,
          view,
          source,
        },
      });
      setRows(response.data?.data || []);
      setTotal(response.data?.meta?.total || 0);
    } catch (error) {
      message.error(t('payments.loadError'));
      setRows([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [page, pageSize, view, source]);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: t('table.driver'),
      key: 'driver',
      render: (_: unknown, record: AtmosPayment) =>
        record.driver ? (
          <div>
            <div style={{ fontWeight: 500 }}>{record.driver.fullName || t('common.noName')}</div>
            <div style={{ fontSize: 12, color: '#7F8C8D' }}>{record.driver.phone}</div>
          </div>
        ) : (
          <span style={{ color: '#7F8C8D' }}>-</span>
        ),
    },
    {
      title: t('common.type'),
      dataIndex: 'transactionType',
      key: 'transactionType',
      render: (type: AtmosPayment['transactionType']) =>
        type === 'WALLET_TOPUP' ? (
          <Tag icon={<ArrowDownOutlined />} color="green">
            {t('payments.topup')}
          </Tag>
        ) : (
          <Tag icon={<ArrowUpOutlined />} color="orange">
            {t('payments.withdrawal')}
          </Tag>
        ),
    },
    {
      title: t('payments.source'),
      dataIndex: 'source',
      key: 'source',
      render: (s: AtmosPayment['source']) => {
        if (s === 'atmos') return <Tag color="blue">{t('payments.sourceAtmos')}</Tag>;
        if (s === 'admin') return <Tag color="purple">{t('payments.sourceAdmin')}</Tag>;
        return <Tag>{t('payments.sourceOther')}</Tag>;
      },
    },
    {
      title: t('common.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (val: number) => <strong>{formatCurrency(val)}</strong>,
    },
    {
      title: t('payments.atmosId'),
      dataIndex: 'atmosId',
      key: 'atmosId',
      render: (atmosId: string | null) =>
        atmosId ? <code>#{atmosId}</code> : <span style={{ color: '#7F8C8D' }}>-</span>,
    },
    {
      title: t('common.description'),
      dataIndex: 'description',
      key: 'description',
      render: (description: string | null) => (
        <span style={{ fontSize: 12, color: '#555' }}>{description || '-'}</span>
      ),
    },
    {
      title: t('table.date'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap style={{ marginBottom: 12 }}>
          <Segmented
            value={view}
            onChange={(v) => {
              setView(v as AtmosView);
              setPage(1);
            }}
            options={[
              { value: 'all', label: t('payments.viewAll') },
              { value: 'topup', label: t('payments.viewTopups') },
              { value: 'withdrawal', label: t('payments.viewWithdrawals') },
            ]}
          />
          <Segmented
            value={source}
            onChange={(v) => {
              setSource(v as AtmosSource);
              setPage(1);
            }}
            options={[
              { value: 'all', label: t('payments.sourceAll') },
              { value: 'atmos', label: t('payments.sourceAtmos') },
              { value: 'admin', label: t('payments.sourceAdmin') },
            ]}
          />
        </Space>
        <Space wrap>
          <Input
            placeholder={t('payments.searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 240 }}
            allowClear
            onPressEnter={fetchPayments}
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs] | null)}
            format="YYYY-MM-DD"
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={fetchPayments}>
            {t('common.search')}
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          dataSource={rows}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
            showTotal: (total) => `${t('common.total')}: ${total}`,
          }}
        />
      </Card>
    </div>
  );
};

export const PaymentList = () => {
  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 24 }}>
        <DollarOutlined style={{ marginRight: 8 }} />
        {t('payments.title')}
      </h2>

      <Tabs
        defaultActiveKey="ledger"
        items={[
          { key: 'ledger', label: t('payments.tabLedger'), children: <WalletLedgerTab /> },
          { key: 'click', label: t('payments.tabClick'), children: <ClickList /> },
          { key: 'paynet', label: t('payments.tabPaynet'), children: <PaynetList /> },
        ]}
      />
    </div>
  );
};
